import { fg } from '@atlaskit/platform-feature-flags';

import { DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI } from '../../../types/default-cloud-graph-document-entity-ati';
import { DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI } from '../../../types/default-cloud-graph-remote-link-entity-ati';
import { DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI } from '../../../types/default-cloud-graph-video-entity-ati';
import type { FilterId } from '../../../types/filters';
import { type ThirdPartyConfig } from '../../../types/schemas/types';
import {
	NounSubtypeKey,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../../../utils/filters/universalType';
import { get3PLogo } from '../../icons';
import { ProductResourceTypes3P } from '../types';

import { GoogleDriveIcon, GoogleDriveIconBorderless, GoogleDriveSearchResultIcon } from './icon';

const getGoogleDriveTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [
			NounSubtypeKey.Document,
			NounSubtypeKey.Pdf,
			NounSubtypeKey.Page,
			NounSubtypeKey.Blogpost,
		],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	if (fg('rovo_fps_enable_video_noun_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: [...DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI],
			subTypes3P: [NounSubtypeKey.Video],
		});
	}

	config.set(TypeFilterValueKey.Image, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Image],
	});
	config.set(TypeFilterValueKey.Folder, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Folder],
	});
	config.set(TypeFilterValueKey.Audio, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Audio],
	});
	config.set(TypeFilterValueKey.Pdf, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Pdf],
	});
	config.set(TypeFilterValueKey.Code, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Code],
	});
	config.set(TypeFilterValueKey.Form, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Form],
	});
	// Back-ported (consolidated) shape — replaces individual Page/Blogpost entries
	// with a richer WebPage entry and folds Archive into Other.
	config.set(TypeFilterValueKey.Notebook, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Notebook],
	});
	config.set(TypeFilterValueKey.WebPage, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
	});
	config.set(TypeFilterValueKey.Other, {
		entities3P: [
			...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
		],
		subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
	});
	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// Keep the entity type aligned to document results so any consumer of typeFilterConfig still
	// treats Google Drive comment matches as documents rather than standalone comments.
	if (fg('enable_3p_embedded_comments_search')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: [
				...DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				...DEFAULT_CLOUD_GRAPH_REMOTE_LINK_ENTITY_ATI,
			],
			subTypes3P: [],
		});
	}

	return config;
};

// static 3P config object for google drive
export const googleDriveConfig: ThirdPartyConfig = {
	displayName: 'Google Drive',
	id: 'drive',
	resourceType: ProductResourceTypes3P.GoogleDrive,
	icon: get3PLogo(GoogleDriveIcon, 'Google Drive', 'medium'),
	iconBorderless: get3PLogo(GoogleDriveIconBorderless, 'Google Drive', 'small'),
	searchResultIcon: get3PLogo(GoogleDriveSearchResultIcon, 'Google Drive', 'large'),
	integrationAri: 'ari:cloud:platform::integration/google',
	// Use a getter to evaluate lazily so that it's evaluated when accessed, not at module load.
	// This allows for feature gate mocks in tests to work correctly
	get typeFilterConfig() {
		return getGoogleDriveTypeFilterConfig();
	},
	get filters(): FilterId[] {
		const baseFilters: FilterId[] = ['lastUpdated', 'onlyTitleMatch'];
		if (fg('rovo_fps_enable_gdrive_space_filter')) {
			baseFilters.push('space');
		}
		if (fg('rovo_fps_enable_gdrive_content_filter')) {
			baseFilters.push('content');
		}
		return baseFilters;
	},
	requiresOauth: true,
	isEnabled: () => true,
};
